"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { fetchAllShopSettings } from "@/store/slices/shopSettingsSlice";
import { fetchGeneralSettings } from "@/store/slices/landingSlice";

const DEFAULT_WHATSAPP_NUMBER = "923137938402";

export default function WhatsAppButton() {
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const { mode } = useAppSelector((s) => s.theme);
  const { data: shopSettingsList } = useAppSelector((s) => s.shopSettings);
  const { data: generalSettings } = useAppSelector((s) => s.landing.generalSettings);
  const [mounted, setMounted] = useState(false);

  const isShopRoute =
    pathname?.startsWith("/shop") ||
    pathname?.startsWith("/cart") ||
    pathname?.startsWith("/checkout") ||
    pathname?.startsWith("/wishlist") ||
    pathname?.startsWith("/orders") ||
    pathname?.startsWith("/order") ||
    pathname?.startsWith("/help") ||
    pathname?.startsWith("/products");

  useEffect(() => {
    setMounted(true);
    if (isShopRoute) {
      if (!shopSettingsList || shopSettingsList.length === 0) {
        dispatch(fetchAllShopSettings());
      }
    } else {
      if (!generalSettings) {
        dispatch(fetchGeneralSettings());
      }
    }
  }, [dispatch, isShopRoute, shopSettingsList, generalSettings]);

  const isDark = mounted && mode === "dark";

  // Dynamic WhatsApp Link Builder
  const getWhatsAppHref = () => {
    if (isShopRoute) {
      const activeShop = shopSettingsList && shopSettingsList.length > 0 ? shopSettingsList[0] : null;
      let shopSocial: any = activeShop?.socialLinks;
      if (typeof shopSocial === "string") {
        try {
          shopSocial = JSON.parse(shopSocial);
        } catch (e) {
          shopSocial = undefined;
        }
      }
      const waSetting = shopSocial?.whatsapp;
      const phoneFallback = activeShop?.shopPhoneNumber && activeShop.shopPhoneNumber.length > 0 ? activeShop.shopPhoneNumber[0] : null;

      if (waSetting && waSetting.trim()) {
        if (waSetting.startsWith("http://") || waSetting.startsWith("https://")) {
          return waSetting;
        }
        const clean = waSetting.replace(/[^0-9]/g, "");
        if (clean) {
          return `https://wa.me/${clean}?text=${encodeURIComponent("Hi! I am shopping on your store and have a question about products/orders.")}`;
        }
      }

      if (phoneFallback && phoneFallback.trim()) {
        const clean = phoneFallback.replace(/[^0-9]/g, "");
        if (clean) {
          return `https://wa.me/${clean}?text=${encodeURIComponent("Hi! I am shopping on your store and have a question about products/orders.")}`;
        }
      }
    }

    // Corporate / General Website Settings
    const genSettingsData = Array.isArray(generalSettings)
      ? generalSettings[generalSettings.length - 1]
      : ((generalSettings as any)?.data || generalSettings);

    let genSocial: any = genSettingsData?.socialLinks;
    if (typeof genSocial === "string") {
      try {
        genSocial = JSON.parse(genSocial);
      } catch (e) {
        genSocial = undefined;
      }
    }
    const genWa = genSocial?.whatsapp;

    if (genWa && genWa.trim()) {
      if (genWa.startsWith("http://") || genWa.startsWith("https://")) {
        return genWa;
      }
      const clean = genWa.replace(/[^0-9]/g, "");
      if (clean) {
        return `https://wa.me/${clean}?text=${encodeURIComponent("Hi Hubmicroo! I'd like to know more about your services.")}`;
      }
    }

    // Fallback to corporate phone number if available
    const rawGenPhones = genSettingsData?.phoneNumber;
    const genPhoneFallback = Array.isArray(rawGenPhones)
      ? rawGenPhones[0]
      : typeof rawGenPhones === "string"
        ? rawGenPhones
        : null;

    if (genPhoneFallback && genPhoneFallback.trim()) {
      const clean = genPhoneFallback.replace(/[^0-9]/g, "");
      if (clean) {
        return `https://wa.me/${clean}?text=${encodeURIComponent("Hi Hubmicroo! I'd like to know more about your services.")}`;
      }
    }

    return `https://wa.me/${DEFAULT_WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi Hubmicroo! I'd like to know more about your services.")}`;
  };

  return (
    <motion.a
      href={getWhatsAppHref()}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with us on WhatsApp"
      initial={{ opacity: 0, scale: 0, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
      transition={{ 
        delay: 0.8,
        opacity: { duration: 0.4 },
        scale: { duration: 0.5, ease: "backOut" },
        y: { repeat: Infinity, duration: 2.8, ease: "easeInOut" }
      }}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.94 }}
      className="group fixed bottom-6 right-6 sm:bottom-7 sm:right-7 z-50 cursor-pointer select-none"
      suppressHydrationWarning
    >
      {/* Pulse Radar Wave */}
      <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-35 pointer-events-none" />
      {/* Ambient Green Glow Aura */}
      <span className="absolute -inset-1 rounded-full bg-gradient-to-r from-[#25D366] to-[#128C7E] blur-lg opacity-40 group-hover:opacity-70 transition-opacity pointer-events-none" />

      {/* Floating Hover Tooltip */}
      <div
        className={`pointer-events-none absolute right-full top-1/2 mr-3.5 -translate-y-1/2 whitespace-nowrap rounded-xl px-3.5 py-2 text-xs font-semibold shadow-xl border transition-all duration-300 opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 hidden sm:flex items-center gap-2 ${
          isDark 
            ? "bg-gray-900/95 border-emerald-500/30 text-white shadow-emerald-950/40" 
            : "bg-white/95 border-emerald-100 text-gray-800 shadow-emerald-600/10"
        } backdrop-blur-md`}
      >
        <span className="w-2 h-2 rounded-full bg-[#25D366] shadow-[0_0_8px_#25D366] animate-pulse" />
        <span>{isShopRoute ? "Need Help? Chat on WhatsApp" : "Chat with us on WhatsApp"}</span>
        <div className={`absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rotate-45 border-r border-t ${
          isDark ? "bg-gray-900 border-emerald-500/30" : "bg-white border-emerald-100"
        }`} />
      </div>

      {/* WhatsApp Action Button */}
      <div
        className="relative flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#25D366] via-[#20BA5A] to-[#128C7E] shadow-lg shadow-[#25D366]/40 ring-4 ring-white dark:ring-gray-900/90 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#25D366]/60"
      >
        {/* Official WhatsApp SVG Icon */}
        <svg 
          viewBox="0 0 24 24" 
          fill="currentColor" 
          className="h-7 w-7 sm:h-8 sm:w-8 text-white drop-shadow-sm transition-transform duration-300 group-hover:scale-110"
        >
          <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
        </svg>

        {/* Live Pulse Dot Badge */}
        <span className="absolute -top-1 -right-1 flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500 ring-2 ring-white dark:ring-gray-900" />
        </span>
      </div>
    </motion.a>
  );
}
